import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    generateCabinetMesh,
    isAppliedPanelWithDepth,
    isAppliedPanelWithLength,
    isAppliedPanelWithWidth,
    isManualPanel,
} from 'components/customer/RoomPlanner/helpers/generateCabinetMesh';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';
import {applyShadows} from 'components/customer/RoomPlanner/helpers/shadows';
import {
    CabinetMesh,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import {ComputedDimension} from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import {store} from 'store/customer';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {updateCabinetAddHistoryTitleRoomCabNumber} from 'components/customer/RoomPlanner/helpers/history/cabinetAddAction';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {generateCuboid} from 'components/customer/RoomPlanner/helpers/cuboid';
import {getRunnerDimensions} from 'components/customer/Preview3DCommon/values/drawer';
import {hasLShapeFootprint} from 'components/customer/Preview3DCommon/helpers/hasLShapeFootprint';
import {Drawer} from 'components/customer/Preview3D/types';
import {normalizeProductDrawers} from 'hooks/Product/helpers/normalizeDrawerValues';
import {triggerIfWireframeMode} from 'components/customer/RoomPlanner/helpers/renderer/cabinetGroupUtils';
import {assignBasicUserData} from 'components/customer/RoomPlanner/helpers/renderer/assignCabinetUserData';
import {assignCabinetMethods} from 'components/customer/RoomPlanner/helpers/renderer/assignCabinetMethods';
import {applyCabinetPositioning} from 'components/customer/RoomPlanner/helpers/renderer/applyCabinetPositioning';
import {
    calculateCabinetInitialPosition,
    toNumberOrDefault,
} from 'components/customer/RoomPlanner/helpers/renderer/cabinetInitialPosition';
import {applyDoorFrontsMode} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';

// Re-export for backward compatibility
export {toNumberOrDefault};

const U_SHAPED_SHELF_TYPE = 9;

// A temporary cuboid only stands in while its product saves; the real
// cabinet replaces it well before this. If it's still around after that
// (e.g. the save failed after the cleanup ran), it's removed.
export const TEMP_MESH_TIMEOUT_MS = 15000;

/**
 * Removes a temporary cuboid after TEMP_MESH_TIMEOUT_MS unless it's
 * already gone.
 * @param {CabinetMesh} cabinetGroup - The temporary cuboid
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef - The
 * room's cabinets
 */
export const scheduleTempMeshRemoval = (
    cabinetGroup: CabinetMesh,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>
) => {
    setTimeout(() => {
        const isListed = cabinetGroupListRef.current.includes(cabinetGroup);
        if (!isListed && !cabinetGroup.parent) return;

        deleteCabinetMesh(cabinetGroup, false, cabinetGroupListRef);
    }, TEMP_MESH_TIMEOUT_MS);
};

// Union of the type-carrying fields across Shelf and its Advanced variants.
type ShelfTypeFields = {
    insert_id?: number;
    shelf_type?: number;
    upper_shelf_type?: number;
    lower_shelf_type?: number;
};

export const DEFAULT_POSITION_Y = {
    isUpperProduct: 300,
    sitOnFloor: 51,
    toeKickHeightDefault: 150,
    tallHeight: 1950,
};

export const renderCabinetGroup = (
    product: RoomPlannerProductWithTemplate,
    dimensions: ComputedDimension,
    room: string,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
    row?: number,
    tempMesh = false
) => {
    const state = store.getState();
    const roomType = state.roomPlanner.roomType;
    const {scale, wallHeight, roomDepthComputed, positionX, defaultPositionZ} =
        dimensions;
    const variables = JSON.parse(
        String(product.template_3d[0].attributes.variables)
    ) as unknown as TemplateVariable;

    const templateName = product.template_3d[0].attributes.template;

    const isBulkheadComponent = templateName?.includes('Bulkhead');
    const isVerminProduct = Boolean(product?.is_vermin);
    const isFillerProduct =
        templateName?.includes('Filler') && !isVerminProduct;
    const innerDrawerOnly = Boolean(variables.innerDrawerOnly);
    const isDrawerFace = product.type_name == 'Drawer Face';

    let cabinetWidth =
        Number(product.cabinet_width) -
        (product.cabinet_carc_thickness ?? 0) -
        (isBulkheadComponent ? (product.cabinet_carc_thickness ?? 0) : 0) +
        (isFillerProduct ? (product.cabinet_carc_thickness ?? 0) : 0) +
        (isVerminProduct ? (product.cabinet_carc_thickness ?? 0) + 1 : 0);
    if (isDrawerFace) {
        cabinetWidth = Number(product.cabinet_panel_width) || 0;
    }
    const cabinetHeight = Number(product.cabinet_height);

    const isLShapedProduct = hasLShapeFootprint(variables);

    const lShapedValues = isLShapedProduct
        ? {
              cabinet_left_width: product.cabinet_left_width ?? product.width2,
              cabinet_right_depth:
                  product.cabinet_right_depth ??
                  product.cabinet_length_depth ??
                  product.length_depth,
              cabinet_right_width: product.cabinet_right_width ?? product.width,
              cabinet_left_depth:
                  product.cabinet_left_depth ??
                  product.cabinet_width_depth ??
                  product.width_depth,
          }
        : {};

    let innerDrawerValues = {};
    if (variables.innerDrawerOnly && product.drawers?.length) {
        const innerDrawer = product.drawers[0] as unknown as Drawer;
        const runnerSpecs =
            product?.drawer_runner_specs ?? innerDrawer.drawer_runner_specs;
        const {height: runnerHeight, depth: runnerDepth} = getRunnerDimensions(
            String(runnerSpecs)
        );
        innerDrawerValues = {
            cabinet_depth: runnerDepth,
            cabinet_height: runnerHeight,
            cabinet_width: product.cabinet_panel_width,
        };
    }

    if (
        product.drawer_amount !== undefined &&
        product.drawer_amount !== 0 &&
        product.drawer_amount !== product.drawers?.length
    ) {
        const normalizedProduct = normalizeProductDrawers(product, true);

        product = {
            ...product,
            ...normalizedProduct,
        };
    }

    // Without a toe kick the cabinet has no adjustable legs (and
    // calculateCabinetInitialPosition sits it on the floor).
    const hasZeroToeKickHeight = product.toe_kick_height == 0;

    let cabinetGroup;
    if (tempMesh) {
        cabinetGroup = generateCuboid(product) as CabinetMesh;
    } else {
        cabinetGroup = generateCabinetMesh({
            ...product,
            ...lShapedValues,
            cabinet_width: cabinetWidth,
            cabinet_height: cabinetHeight,
            cabinet_depth: Number(product.cabinet_depth),
            cabinet_simple_shelves:
                (product as unknown as {cabinet_simple_shelves?: boolean})
                    .cabinet_simple_shelves ?? true,
            ...innerDrawerValues,
            ...(hasZeroToeKickHeight ? {cabinet_adjustable_legs: 0} : {}),
        } as unknown as RoomPlannerProductWithTemplate);
    }

    const isFloorProduct = templateName?.includes('KickerFramed');
    const isLshapedPanel = templateName?.includes('LShapePanel.xml');
    const isTallLShape = templateName?.includes('TallLShape.xml');
    const isKeyShapePanel = templateName?.includes('KeyShapePanel.xml');
    const isPanelProduct =
        isAppliedPanelWithDepth(templateName) ||
        Boolean(variables.isPanelProduct);
    const isPanelProductWithWidth = isAppliedPanelWithWidth(templateName);
    const isPanelProductWithLength = isAppliedPanelWithLength(templateName);
    const isManualPanelProduct = isManualPanel(templateName, variables);

    const isLShapePanel = variables.isLShapePanel;
    const isUShapedPanel = variables.isUShapedPanel;
    // `shelves` are Shelf, `upper_shelves`/`lower_shelves` may be their
    // Advanced variants, which carry a differently-named type field.
    const shelfEntries: ShelfTypeFields[] = [
        ...(Array.isArray(product.shelves) ? product.shelves : []),
        ...(Array.isArray(product.upper_shelves) ? product.upper_shelves : []),
        ...(Array.isArray(product.lower_shelves) ? product.lower_shelves : []),
    ];
    const hasUShapedShelves = shelfEntries.some((shelf) =>
        [
            shelf?.insert_id,
            shelf?.shelf_type,
            shelf?.upper_shelf_type,
            shelf?.lower_shelf_type,
        ].some((value) => Number(value) === U_SHAPED_SHELF_TYPE)
    );
    const isKickerLengthProduct = Boolean(variables.isKickerLengthProduct);

    const sitOnFloor =
        isFloorProduct ||
        cabinetGroup.userData.isRobeProduct ||
        cabinetGroup.userData.noBottom ||
        isKickerLengthProduct;

    // Note: After migration, all upper products should have isUpperProduct flag set
    const isUpperProduct = variables?.isUpperProduct ?? false;

    // Detect cabinet style based on template name: Base=1, Tall=2, Upper=3
    const isTallProduct =
        templateName?.includes('Tall') || templateName?.includes('Pantry');
    const isBaseProduct = !isUpperProduct && !isTallProduct; // Default to Base
    const cabinetStyle = isUpperProduct ? 3 : isTallProduct ? 2 : 1;

    const {width, height, depth} = getGroupWidth(cabinetGroup);

    // Calculate initial cabinet position based on type and properties
    const {
        positionY: initialPositionY,
        positionZ: initialPositionZ,
        toeKickHeight,
        extendsToFloor,
    } = calculateCabinetInitialPosition({
        product,
        cabinetGroup,
        variables,
        templateName,
        state,
        room,
        wallHeight,
        roomDepthComputed,
        defaultPositionZ,
        sitOnFloor,
        isBulkheadComponent,
        isUpperProduct,
        height,
    });

    const positionY = initialPositionY;
    const positionZ = initialPositionZ;

    // Note: After migration, all products with width offset should have hasWidthOffset flag set
    const productsWithWidthOffset = variables?.hasWidthOffset ?? false;

    // Merge the resolved L-shape dimensions (with their fallback keys) so the
    // dimension labels read the real left/right width/depth instead of 0.
    let productValues = {...product, ...lShapedValues};

    if (variables.innerDrawerOnly && product.drawers?.length) {
        // NOTE: this should happen somewhere either when mapping the response from API or from the backend itself
        // I feel we need to consolidate stuffs to map it into just Height, Depth, and Width
        // This will make the dimensions display more robust and render straight forward rather than having set of conditions before rendering
        productValues = {
            ...productValues,
            ...innerDrawerValues,
        };
    }

    if (
        variables.isUShapedPanel ||
        variables.isKeyShapePanel ||
        variables.isLShapePanel
    ) {
        productValues = {
            ...productValues,
            cabinet_height: product.cabinet_carc_thickness,
        };
    }

    // Assign basic user data properties
    assignBasicUserData({
        cabinetGroup,
        product,
        productValues: productValues as RoomPlannerProductWithTemplate,
        variables,
        wallHeight,
        width,
        toeKickHeight,
        isPanelProduct,
        isPanelProductWithWidth,
        isPanelProductWithLength,
        isFloorProduct,
        isLShapedProduct,
        isUpperProduct,
        isBaseProduct,
        isTallProduct,
        cabinetStyle,
        isLShapePanel: isLShapePanel ?? false,
        isManualPanelProduct,
        sitOnFloor,
        tempMesh,
        isFillerProduct,
        isVerminProduct,
        isBulkheadComponent,
        isTallLShape,
        innerDrawerOnly,
        isKeyShapePanel,
        isUShapedPanel: isUShapedPanel ?? false,
        hasUShapedShelves,
        isKickerLengthProduct,
        productsWithWidthOffset,
    });

    // Assign cabinet methods (rotation, duplication, movement, deletion, etc.)
    assignCabinetMethods({
        cabinetGroup,
        cabinetGroupListRef,
        dimensions,
        room,
        roomType: roomType ?? undefined,
        isUpperProduct,
        isManualPanelProduct,
        sitOnFloor,
        renderCabinetGroup,
    });

    // Apply cabinet positioning and rotation logic
    applyCabinetPositioning({
        cabinetGroup,
        product,
        dimensions,
        cabinetGroupListRef,
        roomType: roomType ?? undefined,
        positionX: positionX ?? 0,
        positionY,
        positionZ,
        isManualPanelProduct,
        isKeyShapePanel,
        isLShapedProduct,
        isLshapedPanel,
        isUpperProduct,
        isFillerProduct,
        isPanelProduct,
        isVerminProduct,
        isBulkheadComponent,
        extendsToFloor,
        toeKickHeight,
        tempMesh,
        width,
        depth,
        height,
        scale,
    });

    cabinetGroup.rotation.x = -(90 * Math.PI) / 180;
    cabinetGroup.scale.set(scale, scale, scale);
    applyShadows(cabinetGroup);
    Camera.scene.add(cabinetGroup);

    triggerIfWireframeMode(cabinetGroup);

    const doorFrontsMode = state.roomPlanner.doorFrontsMode;
    void applyDoorFrontsMode(
        cabinetGroup,
        doorFrontsMode,
        (group, eventName, params) =>
            productEvents.triggerEventsAsync(group, eventName, params),
        {
            animate: doorFrontsMode === 'open',
            animationSpeed: 0,
        }
    );

    if (!state.roomPlanner.showCabinets) {
        cabinetGroup.visible = false;
    }

    // If this cabinet corresponds to a CABINET_ADD history entry and we now
    // know the authoritative room_cab_number from the product, update the
    // history title to reflect the correct cabinet number.
    if (
        product.job_cabinet_id != null &&
        product.room_cab_number != null &&
        Number(product.room_cab_number) > 0
    ) {
        updateCabinetAddHistoryTitleRoomCabNumber(
            Number(product.job_cabinet_id),
            Number(product.room_cab_number)
        );
    }

    cabinetGroupListRef.current.push(cabinetGroup);

    productEvents.setProducts(cabinetGroupListRef.current);

    if (tempMesh) {
        scheduleTempMeshRemoval(cabinetGroup, cabinetGroupListRef);
    }

    if (!productEvents.areEventsAttached()) {
        productEvents.attachEvents({
            scene: Camera.scene,
            camera: Camera.camera,
            controls: Camera.controls,
            roomType: roomType ?? 'RECTANGULAR',
        });
    }

    return {
        widthIncrement: width * scale,
        cabinetGroup,
    };
};
